import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Logo from "../components/Logo";
import { track } from "../lib/analytics";
import { saveOnboarding, getToken, setToken, setStoredUser } from "../lib/auth";

const STEPS = [
  { id: "basics", label: "Basics" },
  { id: "intent", label: "Intention" },
  { id: "values", label: "Values" },
  { id: "video", label: "Intro Video" },
  { id: "ready", label: "Ready" },
];

const VALUE_CARDS = [
  "Depth over breadth",
  "Kindness above all",
  "Growth, always",
  "Independence & partnership",
  "Family-forward",
  "Curiosity",
  "Craft & mastery",
  "Spirituality",
  "Service",
];

const IDENTITY_OPTIONS = [
  "Cisgender",
  "Transgender",
  "Non-binary",
  "Genderfluid",
  "Intersex",
  "Two-Spirit",
  "Prefer not to say",
];

const INTENT = [
  "Long-term partnership",
  "Marriage eventually",
  "Something real, unrushed",
  "Open to being surprised",
];

export default function Onboarding() {
  const [step, setStep] = useState(0);
  const [name, setName] = useState("");

  const [birthMonth, setBirthMonth] = useState("");
  const [birthDay,   setBirthDay]   = useState("");
  const [birthYear,  setBirthYear]  = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [zipError, setZipError] = useState("");
  const [zipLoading, setZipLoading] = useState(false);
  const [gender, setGender] = useState("");
  const [preference, setPreference] = useState("");
  const [intent, setIntent] = useState(INTENT[0]);
  const [values, setValues] = useState([VALUE_CARDS[0], VALUE_CARDS[2]]);
  const [error, setError] = useState("");

useEffect(() => {
  if (error) {
    setTimeout(() => {
      window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
    }, 100);
  }
}, [error]);

  const [nameError, setNameError] = useState("");
  const [dobError, setDobError] = useState("");
  const [genderError, setGenderError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [valuesError, setValuesError] = useState("");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [accountSubmitting, setAccountSubmitting] = useState(false);
  const [videoUrl,        setVideoUrl]        = useState("");
  const [videoUploading,  setVideoUploading]  = useState(false);
  const [videoError,      setVideoError]      = useState("");
  const [identity,        setIdentity]        = useState([]);
  const navigate = useNavigate();

  // Analytics: fire once on mount, then on every step transition
  useEffect(() => { track('onboarding_start'); }, []);

  useEffect(() => {
    if (error) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [error]);
 // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { 
if (step !== undefined && step !== null) {
  track('onboarding_step_view', { step: `step_${step}` });
}
 }, [step]);

  const toggle = (arr, set, item) =>
    set(arr.includes(item) ? arr.filter((v) => v !== item) : [...arr, item]);

  const next = async () => {
      if (accountSubmitting) return;
      setError("");
      setNameError("");
      setDobError("");
      setGenderError("");
      setEmailError("");
      setPasswordError("");
      setValuesError("");
    // Step 0 validation
    if (step === 0) {
      if (!name.trim()) {
        setNameError("Required");
        track("onboarding_validation", { step: "basics" });
        return;
      }
      if (!birthMonth || !birthDay || !birthYear) {
        setDobError("Required");
        track("onboarding_validation", { step: "basics_birth" });
        return;
      }
      const bDate = new Date(parseInt(birthYear), parseInt(birthMonth) - 1, parseInt(birthDay));
      if (bDate.getMonth() !== parseInt(birthMonth) - 1) {
        setDobError("Invalid date");
        return;
      }
      const ageCutoff = new Date();
      ageCutoff.setFullYear(ageCutoff.getFullYear() - 18);
      if (bDate > ageCutoff) {
        setError("You must be 18 or older to join BoraMoyo");
        track("onboarding_blocked", { step: "age_restriction" });
        return;
      }
      if (zipLoading) {
          setZipError("Please wait while we verify your ZIP code");
          track("onboarding_validation", { step: "basics_zip_loading" });
          return;
        }
        if (!zip || zip.length !== 5 || zipError || !city || !state) {
        setZipError(zipError || "Please enter a valid 5-digit ZIP code");
        setError("");
        track("onboarding_validation", { step: "basics_zip" });
        return;
      }
      if (!gender || !preference) {
        setGenderError("Required");
        track("onboarding_validation", { step: "basics_gender" });
        return;
      }
      setError("");
    }

    // Step 2 validation (values) + early account creation
    if (step === 2) {
      if (values.length < 3 || values.length > 5) {
        setValuesError("Please select at least 3 non-negotiables to continue.");
        setError("Please select at least 3 non-negotiables to continue.");
        track("onboarding_validation", { step: "values" });
        if (email && email.includes("@")) {
          fetch("/api/onboarding/attempt", { method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email: email.trim(), step: 2, error: "values_insufficient", values_selected: values.length, honeypot }) }).catch(() => {});
        }
        return;
      }
      const cleanEmail = email.trim();
        if (!cleanEmail) {
          setEmailError("Email is required");
          setError("Email and password are required to continue");
          return;
        }
        if (!/^\S+@\S+\.\S+$/.test(cleanEmail)) {
          setEmailError("Enter a valid email address");
          setError("Enter a valid email address");
          return;
        }
        if (!password || !confirmPassword) {
        setPasswordError(!password ? "Password is required" : "Please confirm your password");
        setError("Password and confirm password are required");
        return;
      }
      if (password.length < 8) {
        setPasswordError("Password must be at least 8 characters");
        setError("Password must be at least 8 characters");
        return;
      }
      if (password !== confirmPassword) {
        setPasswordError("Passwords do not match");
          setError("Passwords do not match");
        return;
      }
      if (!getToken()) {
        setAccountSubmitting(true);
        try {
          const reg = await fetch("/api/register", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email, password }),
          });
          const regData = await reg.json();
          if (!reg.ok) {
            const msg = String(regData.detail || "").toLowerCase().includes("email")
                ? "That email is already in use. You can sign in or recover your account below."
                : (regData.detail || "Account creation failed. Please try again.");
              setEmailError(msg);
              setError(msg);
            return;
          }
          if (regData.token) {
            setToken(regData.token);
            setStoredUser(regData);
          }
        } catch {
          setError("Account creation failed. Check your connection.");
          track("onboarding_blocked", { step: "api_error" });
          return;
        } finally {
          setAccountSubmitting(false);
        }
      }
    }

    // Step 3 validation (intro video)
    if (step === 3) {
      if (!videoUrl) {
        setError("Intro video required to complete profile");
        track("onboarding_validation", { step: "video" });
        return;
      }
    }

    setError("");
    track("onboarding_continue", { step });
    const nextStep = Math.min(step + 1, STEPS.length - 1);
    if (email && email.includes("@")) {
      fetch("/api/onboarding/funnel", { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), step: nextStep }) }).catch(() => {});
    }
    setStep(() => nextStep);
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const lookupZip = async (raw) => {
    const zip5 = String(raw || "").replace(/\D/g, "").slice(0, 5);
    setZip(zip5);
    if (zip5.length < 5) {
      setZipError("");
      setCity("");
      setState("");
      return;
    }
    setZipLoading(true);
    setZipError("");
    try {
      const res = await fetch(`/api/zip/lookup?zip=${zip5}`);
      const data = await res.json();
      if (!res.ok || !data?.ok) {
        setCity("");
        setState("");
        setZipError(data?.error || "Invalid ZIP code");
      } else {
        setCity(data.city || "");
        setState(data.state || "");
        setZipError("");
      }
    } catch {
      setCity("");
      setState("");
      setZipError("ZIP lookup failed — try again");
    } finally {
      setZipLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-hero text-white flex flex-col">
      {/* GLOBAL_ERROR_BLOCK */}
      {error && (
        <div style={{
          background: '#ff4d4f',
          color: 'white',
          padding: '12px',
          margin: '16px auto 0',
          borderRadius: '8px',
          fontWeight: '600',
          textAlign: 'center',
          maxWidth: '720px',
          width: 'calc(100% - 32px)'
        }}>
          {error}
        </div>
      )}
      {/* Top bar */}
      <div className="px-6 md:px-12 pt-7 flex items-center justify-between">
        <Logo size="sm" />
        <Link to="/" className="text-xs text-white/50 hover:text-white">
          Save & exit
        </Link>
      </div>

      {/* Progress */}
      <div className="mx-auto w-full max-w-3xl px-6 md:px-12 mt-10">
        <div className="flex items-center gap-2">
          {STEPS.map((s, i) => (
            <div key={s.id} className="flex-1">
              <div
                className={`h-[3px] rounded-full transition-all ${
                  i <= step ? "bg-gradient-gold" : "bg-white/10"
                }`}
              />
              <div
                className={`text-[10px] tracking-[0.22em] uppercase mt-2 ${
                  i === step ? "text-[#F2DE94]" : "text-white/35"
                }`}
              >
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 grid place-items-center px-6 md:px-12 py-10">
        <div
          key={step}
          data-testid={`onboarding-step-${STEPS[step].id}`}
          className="w-full max-w-2xl glass-strong rounded-3xl p-8 md:p-12 animate-fade-up"
        >
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-3">
            Step {step + 1} of {STEPS.length}
          </div>

            {step === 0 && (
              <>
              <h1 className="font-display text-4xl md:text-5xl leading-tight">
                Tell us who's arriving.
              </h1>
              <p className="text-white/55 mt-3">
                The essentials — we'll keep them private and minimal.
              </p>
              <div className="mt-8 grid md:grid-cols-2 gap-4">
                <Field required error={nameError} label="First name" value={name} onChange={(v) => { setName(v); setNameError(""); }} placeholder="First name" testid="onboarding-name" />

                <div>
                  <div className="text-[11px] tracking-[0.2em] uppercase text-white/55 mb-2">Date of birth</div>
                  <div className="grid grid-cols-3 gap-2">
                    <select
                      data-testid="onboarding-birth-month"
                      value={birthMonth}
                      onChange={(e) => setBirthMonth(e.target.value)}
                      style={{ colorScheme: "dark", backgroundColor: "#0b1220", color: "#ffffff" }}
                      className="w-full bg-[#0b1220] border border-white/10 focus:border-[rgba(212,175,55,0.5)] outline-none rounded-xl px-3 py-3 text-white"
                    >
                      <option value="">Month</option>
                      {["January","February","March","April","May","June","July","August","September","October","November","December"].map((m, i) => (
                        <option key={m} value={String(i + 1)}>{m}</option>
                      ))}
                    </select>
                    <select
                      data-testid="onboarding-birth-day"
                      value={birthDay}
                      onChange={(e) => setBirthDay(e.target.value)}
                      style={{ colorScheme: "dark", backgroundColor: "#0b1220", color: "#ffffff" }}
                      className="w-full bg-[#0b1220] border border-white/10 focus:border-[rgba(212,175,55,0.5)] outline-none rounded-xl px-3 py-3 text-white"
                    >
                      <option value="">Day</option>
                      {Array.from({ length: 31 }, (_, i) => (
                        <option key={i + 1} value={String(i + 1)}>{i + 1}</option>
                      ))}
                    </select>
                    <select
                      data-testid="onboarding-birth-year"
                      value={birthYear}
                      onChange={(e) => setBirthYear(e.target.value)}
                      style={{ colorScheme: "dark", backgroundColor: "#0b1220", color: "#ffffff" }}
                      className="w-full bg-[#0b1220] border border-white/10 focus:border-[rgba(212,175,55,0.5)] outline-none rounded-xl px-3 py-3 text-white"
                    >
                      <option value="">Year</option>
                      {Array.from({ length: 82 }, (_, i) => {
                        const y = new Date().getFullYear() - 18 - i;
                        return <option key={y} value={String(y)}>{y}</option>;
                      })}
                    </select>
                  </div>
                    {dobError && (
                      <div className="text-red-400 text-sm mt-1.5">{dobError}</div>
                    )}
                </div>
                <div className="md:col-span-2">
                  <Field label="ZIP Code" value={zip} onChange={lookupZip} placeholder="75201" testid="onboarding-zip" />
                  {zipError && (
                    <div className="text-red-400 text-sm mt-1.5" data-testid="onboarding-zip-error">{zipError}</div>
                  )}
                  {zipLoading && !zipError && (
                    <div className="text-white/40 text-xs mt-1.5">Looking up…</div>
                  )}
                </div>
                <Field label="City" value={city} onChange={() => {}} placeholder="Auto-filled from ZIP" testid="onboarding-city" readOnly />
                <Field label="State" value={state} onChange={() => {}} placeholder="Auto-filled from ZIP" testid="onboarding-state" readOnly />
              </div>

              <div className="mt-6 grid md:grid-cols-2 gap-4">
                <div>
                  <div className="text-[11px] tracking-[0.2em] uppercase text-white/55 mb-2">I am</div>
                  <div className="flex gap-2">
                    {["Male", "Female"].map((g) => (
                      <button
                        key={g}
                        type="button"
                        data-testid={`gender-${g.toLowerCase()}`}
                        onClick={() => { setGender(g.toLowerCase()); setGenderError(""); }}
                        className={`flex-1 rounded-xl px-4 py-3 border text-sm transition-all ${
                          gender === g.toLowerCase()
                            ? "border-[rgba(212,175,55,0.5)] bg-[rgba(212,175,55,0.06)] text-[#F2DE94]"
                            : "border-white/10 text-white/60 hover:border-white/25"
                        }`}
                      >
                        {g}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] tracking-[0.2em] uppercase text-white/55 mb-2">Interested in</div>
                  <div className="flex gap-2">
                    {["Male", "Female", "Both"].map((p) => (
                      <button
                        key={p}
                        type="button"
                        data-testid={`preference-${p.toLowerCase()}`}
                        onClick={() => { setPreference(p.toLowerCase()); setGenderError(""); }}
                        className={`flex-1 rounded-xl px-3 py-3 border text-sm transition-all ${
                          preference === p.toLowerCase()
                            ? "border-[rgba(212,175,55,0.5)] bg-[rgba(212,175,55,0.06)] text-[#F2DE94]"
                            : "border-white/10 text-white/60 hover:border-white/25"
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

                {genderError && (
                  <div className="text-red-400 text-sm mt-2 md:col-span-2">{genderError}</div>
                )}

              {/* Identity — optional multi-select */}
              <div className="mt-6">
                <div className="text-[11px] tracking-[0.2em] uppercase text-white/55 mb-2">
                  Identity <span className="text-white/25 normal-case tracking-normal text-[10px]">· optional</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {IDENTITY_OPTIONS.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setIdentity((prev) =>
                        prev.includes(opt) ? prev.filter((v) => v !== opt) : [...prev, opt]
                      )}
                      className={`rounded-full px-3 py-1.5 text-sm border transition-all ${
                        identity.includes(opt)
                          ? "border-[rgba(212,175,55,0.5)] bg-[rgba(212,175,55,0.06)] text-[#F2DE94]"
                          : "border-white/10 text-white/50 hover:border-white/25"
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <h1 className="font-display text-4xl md:text-5xl leading-tight">
                What brings you here?
              </h1>
              <p className="text-white/55 mt-3">
                There are no wrong answers. Only honest ones.
              </p>
              <div className="mt-8 grid gap-3">
                {INTENT.map((i) => (
                  <button
                    key={i}
                    data-testid={`intent-${i.replace(/\s/g, "-")}`}
                    onClick={() => setIntent(i)}
                    className={`text-left rounded-2xl p-5 border transition-all ${
                      intent === i
                        ? "border-[rgba(212,175,55,0.5)] bg-[rgba(212,175,55,0.05)] glow-gold-soft"
                        : "border-white/10 hover:border-white/25 bg-white/[0.02]"
                    }`}
                  >
                    <div className="font-display text-lg">{i}</div>
                  </button>
                ))}
              </div>
            </>
          )}

            {step === 2 && (
              <>
              <h1 className="font-display text-4xl md:text-5xl leading-tight">
                Choose your quiet non-negotiables.
              </h1>
              <p className="text-white/55 mt-3">Pick three to five that feel truest.</p>
                <div className={`text-xs mt-2 ${values.length >= 3 ? "text-emerald-400" : "text-white/45"}`}>
                  {values.length >= 3 ? `${values.length} selected ✓` : `Choose ${3 - values.length} more to continue`}
                </div>
                {/* honeypot — must stay empty */}
                <input name="website" value={honeypot} onChange={(e) => setHoneypot(e.target.value)}
                  tabIndex="-1" autoComplete="off" aria-hidden="true" style={{ display: "none" }} />
                {valuesError && (
                  <div className="border border-red-400 text-red-300 text-sm mt-3 px-4 py-3 rounded-xl" style={{ backgroundColor: "rgba(239,68,68,0.08)" }} data-testid="values-error">
                    {valuesError}
                  </div>
                )}
              <div className="mt-7 flex flex-wrap gap-2">
                {VALUE_CARDS.map((v) => {
                  const on = values.includes(v);
                  return (
                    <button
                      key={v}
                      onClick={() => { toggle(values, setValues, v); setValuesError(""); setError(""); }}
                      data-testid={`value-${v.split(" ")[0].toLowerCase()}`}
                      className={`rounded-full px-4 py-2 text-sm border transition-all ${
                        on
                          ? "border-[rgba(212,175,55,0.5)] bg-[rgba(212,175,55,0.08)] text-[#F2DE94]"
                          : "border-white/10 text-white/75 hover:border-white/30"
                      }`}
                    >
                      {v}
                    </button>
                  );
                })}
              </div>

              <div className="mt-8 grid gap-4">
                <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-1">Create your account</div>
                <Field required error={emailError} label="Email" value={email} onChange={(v) => { setEmail(v); setEmailError(""); }} placeholder="you@email.com" testid="onboarding-email" />
                <Field required error={passwordError} label="Password" value={password} onChange={(v) => { setPassword(v); setPasswordError(""); }} placeholder="Create a password (min 8 chars)" testid="onboarding-password" type="password" />
                <Field required error={passwordError} label="Confirm Password" value={confirmPassword} onChange={(v) => { setConfirmPassword(v); setPasswordError(""); }} placeholder="Confirm password" testid="onboarding-confirm-password" type="password" />
                {accountSubmitting && <div className="text-sm text-white/50">Creating account…</div>}
              </div>
            </>
          )}

          {step === 4 && (
            <>
              <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-3">
                Ready
              </div>
              <h1 className="font-display text-5xl md:text-6xl leading-tight">
                {name || "You"} — your match pool is being prepared.
              </h1>
              <p className="text-white/65 mt-5 text-lg">
                We'll curate a premium pool from your values and intention.
                Then it's yours to shortlist.
              </p>
              <div className="mt-8 grid md:grid-cols-2 gap-3 text-sm">
                <Summary label="Intention" value={intent} />
                <Summary label="Values" value={`${values.length} chosen`} />
              </div>
            </>
          )}


          {step === 3 && (
            <>
              <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-3">Intro Video</div>
              <h1 className="font-display text-4xl md:text-5xl leading-tight">Show who you are.</h1>
              <p className="text-white/55 mt-4 text-base">
                A short intro video helps your matches connect with you before they reach out.
                Keep it under 2 minutes — just talk naturally.
              </p>

              {videoError && (
                <div className="text-red-400 text-sm mt-4" data-testid="video-error">{videoError}</div>
              )}

              {videoUrl ? (
                <div className="mt-6 rounded-2xl overflow-hidden border border-[rgba(212,175,55,0.3)] relative">
                  <video
                    src={videoUrl}
                    controls
                    className="w-full max-h-72 object-cover bg-black"
                  />
                  <div className="flex items-center justify-between px-4 py-3 bg-[rgba(212,175,55,0.06)]">
                    <span className="text-[#F2DE94] text-sm font-medium">✓ Video uploaded</span>
                    <button
                      type="button"
                      onClick={() => setVideoUrl("")}
                      className="text-white/40 hover:text-white text-xs transition-colors"
                    >
                      Replace
                    </button>
                  </div>
                </div>
              ) : (
                <label
                  data-testid="video-upload-label"
                  className="mt-6 flex flex-col items-center justify-center gap-4 rounded-2xl border-2 border-dashed border-white/15 hover:border-[rgba(212,175,55,0.4)] transition-colors cursor-pointer p-10 text-center"
                >
                  <div className="h-14 w-14 rounded-full bg-[rgba(212,175,55,0.08)] flex items-center justify-center text-2xl text-[#F2DE94]">
                    {videoUploading ? (
                      <span className="animate-spin text-base">⟳</span>
                    ) : (
                      <span>▶</span>
                    )}
                  </div>
                  <div>
                    <div className="font-medium text-white">
                      {videoUploading ? "Uploading…" : "Upload intro video"}
                    </div>
                    <div className="text-white/40 text-sm mt-1">MP4 · MOV · WebM — max 100 MB</div>
                  </div>
                  <input
                    data-testid="video-upload-input"
                    type="file"
                    accept="video/*"
                    capture="user"
                    className="hidden"
                    disabled={videoUploading}
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      if (f.size > 100 * 1024 * 1024) {
                        setVideoError("File too large — max 100 MB");
                        return;
                      }
                      // Check duration before uploading
                      setVideoError("");
                      try {
                        const duration = await new Promise((resolve, reject) => {
                          const vid = document.createElement("video");
                          vid.preload = "metadata";
                          const url = URL.createObjectURL(f);
                          vid.onloadedmetadata = () => { URL.revokeObjectURL(url); resolve(vid.duration); };
                          vid.onerror = () => { URL.revokeObjectURL(url); reject(new Error("unreadable")); };
                          vid.src = url;
                        });
                        if (duration > 15) {
                          setVideoError("Video must be 15 seconds or less");
                          return;
                        }
                      } catch {
                        // If duration unreadable let backend enforce
                      }
                      setVideoUploading(true);
                      try {
                        const fd = new FormData();
                        fd.append("file", f);
                        const token = getToken() || "";
                        const res = await fetch("/api/profile/intro-video", {
                          method: "POST",
                          headers: token ? { Authorization: `Bearer ${token}` } : {},
                          body: fd,
                        });
                        if (!res.ok) {
                          const d = await res.json().catch(() => ({}));
                          setVideoError(d.detail || "Upload failed — please try again");
                          return;
                        }
                        const data = await res.json();
                        setVideoUrl(data.video_url);
                      } catch {
                        setVideoError("Upload failed — check your connection");
                      } finally {
                        setVideoUploading(false);
                      }
                    }}
                  />
                </label>
              )}
            </>
          )}

          {error && (
            <div className="mt-6 border border-red-400 text-red-300 text-sm px-4 py-3 rounded-xl" style={{ backgroundColor: "rgba(239,68,68,0.08)" }} data-testid="onboarding-error">
              {error}
              <div className="mt-3 flex gap-3">
                <a href={`/login?email=${encodeURIComponent(email)}`} className="text-sm text-[#F2DE94] underline">Sign in</a>
                <a href={`/forgot-password?email=${encodeURIComponent(email)}`} className="text-sm text-white/70 underline">Recover account</a>
              </div>
            </div>
          )}

          <div className="mt-10 flex items-center justify-between">
            <button
              data-testid="onboarding-back"
              onClick={back}
              disabled={step === 0}
              className="text-sm text-white/60 hover:text-white disabled:opacity-30 px-3 py-2"
            >
              ← Back
            </button>
            {step < STEPS.length - 1 ? (
              <button
                data-testid="onboarding-next"
                onClick={next} disabled={accountSubmitting || videoUploading || zipLoading || (step === 2 && values.length < 3)}
                className={`rounded-full bg-gradient-gold text-[#080B14] font-semibold px-6 py-3 glow-gold-soft transition-opacity ${step === 2 && values.length < 3 ? "opacity-40 cursor-not-allowed" : ""}`}
              >
                Continue →
              </button>
            ) : (
              <button
                data-testid="onboarding-finish"
                disabled={accountSubmitting}
                onClick={async () => {
                    // Final validation
      if (!videoUrl) {
        setError("Intro video required to complete profile");
        track("onboarding_validation", { step: "final_video" });
        return;
      }
      if (!name || !zip || !city || !state) {
        setError("Please complete your name and ZIP code before continuing.");
        track("onboarding_blocked", { step: "final" });
        return;
      }

      setError("");
      
if (!window.__bm_complete_fired) {
  window.__bm_complete_fired = true;
  track("onboarding_complete");
}

      setAccountSubmitting(true);

      try {
        const calcAge = String(Math.floor(
          (Date.now() - new Date(parseInt(birthYear), parseInt(birthMonth) - 1, parseInt(birthDay)).getTime())
          / (365.25 * 24 * 60 * 60 * 1000)
        ));
        const res = await saveOnboarding({ name, age: calcAge, birth_month: birthMonth, birth_day: birthDay, birth_year: birthYear, city, state, zip, gender, preference, identity, intent, values });
        if (!res) {
          setError("Failed to save onboarding. Please try again.");
          return;
        }

        localStorage.setItem("bm_onboarding_complete", "true");
        
if (!window.__bm_complete_fired) {
  window.__bm_complete_fired = true;
  track("onboarding_complete");
}

        window.location.href = "/membership";
      } catch (err) {
        console.error(err);
        setError("Failed to create account. Please try again.");
      } finally {
        setAccountSubmitting(false);
      }
                  }}
                  className={`rounded-full bg-gradient-gold text-[#080B14] font-semibold px-6 py-3 glow-gold transition-opacity ${accountSubmitting ? "opacity-60" : ""}`}
              >
                {accountSubmitting ? "Creating account…" : "Enter the pool"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testid, className = "", type = "text", readOnly = false, required = false, error = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="text-[11px] tracking-[0.2em] uppercase text-white/55 mb-2">{label} {required && <span className="text-red-400">*</span>}</div>
      <input
        type={type}
        value={value}
        onChange={readOnly ? undefined : (e) => onChange(e.target.value)}
        readOnly={readOnly}
        placeholder={placeholder}
        data-testid={testid}
        className={`w-full bg-white/[0.03] border outline-none rounded-xl px-4 py-3 text-white placeholder:text-white/30 transition-colors ${
          error ? "border-red-400" : "border-white/10"
        } ${readOnly ? "opacity-60 cursor-default" : "focus:border-[rgba(212,175,55,0.5)]"}`}
      />
      {error && <div className="text-red-400 text-xs mt-1" role="alert">{error}</div>}
    </label>
  );
}

function Summary({ label, value }) {
  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/5 p-4">
      <div className="text-[10px] tracking-[0.22em] uppercase text-white/50">{label}</div>
      <div className="font-display mt-1">{value}</div>
    </div>
  );
}